import React, { useMemo } from 'react'
import { MenuDownIcon, TrashDeleteIcon } from 'assests/GmsIcons';

type SortOrderOption = 'ASC' | 'DESC';

type AttributeOption = {
    id: string;
    name: string;
};

type AttributeSortingItemProps = {
    item: AttributeSortItem;
    index: number;
    attributeOptions: AttributeOption[];
    allSelectedItems: AttributeSortItem[];
    onDelete: (id: string) => void;
    onAttributeChange: (id: string, attributeId: string, attributeName: string) => void;
    onOrderChange: (id: string, order: SortOrderOption) => void;
};

export const AttributeSortingItem: React.FC<AttributeSortingItemProps> = ({
    item,
    index,
    attributeOptions,
    allSelectedItems,
    onDelete,
    onAttributeChange,
    onOrderChange,
}) => {
    // Filter out already selected attributes, but keep the current item's selection
    const filteredOptions = useMemo(() => {
        const selectedAttributeIds = allSelectedItems
            .filter(selectedItem => selectedItem.id !== item.id)
            .map(selectedItem => selectedItem.attribute_id)
            .filter(Boolean);

        return attributeOptions.filter(option =>
            !selectedAttributeIds.includes(option.id)
        );
    }, [attributeOptions, allSelectedItems, item.id]);

    const handleAttributeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedOption = attributeOptions.find(option => option.id === e.target.value);
        if (selectedOption) {
            onAttributeChange(item.id, selectedOption.id, selectedOption.name);
        }
    };

    const handleOrderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        onOrderChange(item.id, e.target.value as SortOrderOption);
    };

    return (
        <div className="tw-w-full tw-h-16 tw-relative" data-testid={`attribute-sorting-item-${index}`}>
            <div className="tw-w-full tw-h-16 tw-left-0 tw-top-0 tw-absolute tw-bg-gray-100 tw-shadow-[0px_2px_4px_0px_rgba(116,116,116,0.15)] tw-border tw-border-zinc-300">
                <div className="tw-w-full tw-h-10 tw-py-2 tw-flex tw-items-center tw-justify-between tw-mt-3 tw-px-4">
                    <div className="tw-flex tw-items-center">
                        <div className="tw-w-8 tw-justify-start tw-text-blue-950 tw-text-sm tw-font-semibold tw-ps-2">
                            {index + 1}
                        </div>

                        <div className="tw-w-64 tw-ml-2">
                            <div className="tw-relative">
                                <select
                                    className="tw-block tw-w-full tw-px-2.5 tw-py-2 tw-text-sm tw-bg-white tw-rounded tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-zinc-200 tw-appearance-none focus:tw-outline-blue-500"
                                    value={item.attribute_id || ''}
                                    onChange={handleAttributeChange}
                                    data-testid={`attribute-select-${index}`}
                                >
                                    <option value="">Select Attribute</option>
                                    {filteredOptions.map(option => (
                                        <option key={option.id} value={option.id}>
                                            {option.name}
                                        </option>
                                    ))}
                                </select>
                                <div className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-px-2 tw-pointer-events-none">
                                    <MenuDownIcon />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="tw-w-48 tw-mx-8">
                        <div className="tw-relative">
                            <select
                                className="tw-block tw-w-full tw-px-2.5 tw-py-2 tw-text-sm tw-bg-white tw-rounded tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-zinc-200 tw-appearance-none focus:tw-outline-blue-500"
                                value={item.order || 'ASC'}
                                onChange={handleOrderChange}
                                data-testid={`order-select-${index}`}
                            >
                                <option value="ASC">Ascending</option>
                                <option value="DESC">Descending</option>
                            </select>
                            <div className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-px-2 tw-pointer-events-none">
                                <MenuDownIcon />
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-text-gray-500 hover:tw-text-red-500"
                        onClick={() => onDelete(item.id)}
                        data-testid={`delete-button-${index}`}
                    >
                        <TrashDeleteIcon />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AttributeSortingItem